import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiService, appSettingsService } from "@/lib/api-service";
import { parseValidationErrors } from "@/lib/parse-validation-errors.util";
import { useAuth } from "@/lib/auth-context";
import { UserRole } from "@employee-management/common";
import type { CreateEmployeeInput } from "@/types/employee.types";
import type { Employee } from "@/types/employee.types";
import type { AppSettings } from "@/types/app-settings.types";
import React from "react";

// Define the schema locally
const EditEmployeeSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  department: z.string().min(1, "Department is required"),
  hourlyRate: z.number().min(0, "Hourly rate must be positive"),
  internalEmployeeId: z.string().optional(),
  isActive: z.boolean(),
});

interface EmployeeEditDialogProps {
  employee: Employee | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EmployeeEditDialog({
  employee,
  open,
  onOpenChange,
}: EmployeeEditDialogProps) {
  const { hasRole } = useAuth();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    setError,
    getFieldState,
  } = useForm<CreateEmployeeInput>({
    resolver: zodResolver(EditEmployeeSchema),
    defaultValues: {
      name: "",
      email: "",
      department: "",
      hourlyRate: 0,
      internalEmployeeId: "",
      isActive: true,
    },
  });

  // Fetch departments from app settings
  const { data: appSettings } = useQuery<AppSettings>({
    queryKey: ["app-settings"],
    queryFn: () => appSettingsService.getAll(),
  });

  // Create department options for combobox
  const departmentOptions: ComboboxOption[] = (
    appSettings?.department_list || []
  ).map((dept) => ({
    value: dept,
    label: dept,
  }));

  const updateEmployeeMutation = useMutation({
    mutationFn: (data: CreateEmployeeInput) =>
      apiService.put<Employee>(`/employees/${employee?.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employees"] });
      onOpenChange(false);
    },
    onError: (error: unknown) => {
      parseValidationErrors<CreateEmployeeInput>(
        error,
        setError,
        getFieldState
      );

      toast.error("Failed to update employee");
    },
  });

  function onSubmit(data: CreateEmployeeInput) {
    if (employee) {
      updateEmployeeMutation.mutate(data);
    }
  }

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen);
    if (!newOpen) {
      // Reset form when dialog is closed
      reset();
    }
  };

  // Initialize form data when employee changes
  React.useEffect(() => {
    if (employee) {
      reset({
        name: employee.name,
        email: employee.email,
        department: employee.department,
        hourlyRate: employee.hourlyRate,
        internalEmployeeId: employee.internalEmployeeId || "",
        isActive: employee.isActive,
      });
    }
  }, [employee, reset]);

  const selectedDepartment = watch("department");
  const isActive = watch("isActive");

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Employee</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <Label required  htmlFor="name">Name</Label>
              <Input
                id="name"
                {...register("name")}
                placeholder="Enter employee name"
              />
              {errors.name?.message && (
                <p className="text-sm text-destructive">
                  {errors.name.message as string}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label required htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                {...register("email")}
                placeholder="Enter employee email"
              />
              {errors.email?.message && (
                <p className="text-sm text-destructive">
                  {errors.email.message as string}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <Label required  htmlFor="department">Department</Label>
              <Combobox
                options={departmentOptions}
                value={selectedDepartment}
                onValueChange={(value) => setValue("department", value)}
                placeholder="Select department..."
                searchPlaceholder="Search departments..."
                emptyText="No departments found."
              />
              {errors.department?.message && (
                <p className="text-sm text-destructive">
                  {errors.department.message as string}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label  htmlFor="internalEmployeeId">Internal Employee ID</Label>
              <Input
                id="internalEmployeeId"
                {...register("internalEmployeeId")}
                placeholder="Enter internal employee ID (optional)"
              />
              {errors.internalEmployeeId?.message && (
                <p className="text-sm text-destructive">
                  {errors.internalEmployeeId.message as string}
                </p>
              )}
            </div>
            {/* Only show hourly rate field for managers and superadmins */}
            {hasRole(UserRole.MANAGER) && (
              <div className="space-y-2">
                <Label required htmlFor="hourlyRate">Hourly Rate</Label>
                <Input
                  id="hourlyRate"
                  type="number"
                  step="0.01"
                  {...register("hourlyRate", { valueAsNumber: true })}
                  placeholder="Enter hourly rate"
                />
                {errors.hourlyRate?.message && (
                  <p className="text-sm text-destructive">
                    {errors.hourlyRate.message as string}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label required  htmlFor="status">Status</Label>
            <Select
              value={isActive.toString()}
              onValueChange={(value) => setValue("isActive", value === "true")}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Active</SelectItem>
                <SelectItem value="false">Inactive</SelectItem>
              </SelectContent>
            </Select>
            {errors.isActive?.message && (
              <p className="text-sm text-destructive">
                {errors.isActive.message as string}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={updateEmployeeMutation.isPending}>
              {updateEmployeeMutation.isPending
                ? "Updating..."
                : "Update Employee"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
